/**
 * Frontier Chat - P2 OBS-15 (2026-05-27).
 *
 * Conversational surface for the DPO / Compliance Analyst to ask
 * questions grounded on this tenant's posture: top risks, breach
 * incidents, DSAR flows, scan coverage, detection decisions.
 *
 * Today the responses are deterministic; once a production
 * KmsKeyResolver lands, the registered BYO-LLM key (Tier 5) powers
 * live AI responses. The UI surface is the same either way.
 */
import { Rx, useRegulationText } from '@/lib/regulation-terms';
import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { MessageSquare, Send, Sparkles, Info } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { frontierChat, type ChatMessage } from '@/lib/api';

const SAMPLE_QUESTIONS = [
  'What are my top risks right now?',
  'How is my DPDPA posture?',
  'How do I handle a bulk DSAR import?',
  'Show me recent detection decisions',
  'What breaches are open?',
  'How do I onboard a new data source?',
];

export function FrontierChat(): JSX.Element {
  const tx = useRegulationText();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modelHint, setModelHint] = useState<string | null>(null);
  const [liveAi, setLiveAi] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  async function send(question?: string): Promise<void> {
    const q = (question ?? input).trim();
    if (!q || sending) return;
    setError(null);
    setInput('');
    const next: ChatMessage[] = [...messages, { role: 'user', content: q }];
    setMessages(next);
    setSending(true);
    try {
      const result = await frontierChat(next);
      setMessages([...next, { role: 'assistant', content: result.content }]);
      setModelHint(result.modelHint);
      setLiveAi(result.liveAi);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chat request failed');
    } finally {
      setSending(false);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="FOCTTA Assistant"
        subtitle={tx("Ask questions grounded on your tenant - top risks, breach incidents, DSAR flows, scan coverage, decisions. Tier 5 BYO-LLM powered.")}
        badges={[
          { label: 'Tier 5 BYO-LLM', variant: 'glass' },
          {
            label: liveAi ? 'Live AI' : 'Deterministic (BYO-LLM key not wired)',
            variant: liveAi ? 'success' : 'warning',
          },
          ...(modelHint ? [{ label: modelHint, variant: 'info' as const }] : []),
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Chat panel */}
        <GlassCard className="lg:col-span-2 p-0 flex flex-col" style={{ minHeight: 540 }}>
          <CardHeader className="px-5 py-4 border-b border-border/40">
            <CardTitle className="flex items-center gap-2 text-sm">
              <MessageSquare className="h-4 w-4 text-primary" /> Conversation
            </CardTitle>
            <CardDescription>
              {messages.length === 0
                ? 'Start with one of the suggested questions, or ask your own.'
                : `${messages.length} message${messages.length === 1 ? '' : 's'} this session`}
            </CardDescription>
          </CardHeader>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-5 py-4 space-y-3"
            style={{ maxHeight: 480 }}
          >
            {messages.length === 0 && (
              <div className="text-center text-muted-foreground text-xs py-8">
                <Sparkles className="h-6 w-6 mx-auto mb-2 opacity-50" />
                Ask anything about this tenant&apos;s posture.
              </div>
            )}
            {messages.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 6, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ type: 'spring', stiffness: 360, damping: 28 }}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-lg text-xs leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-primary text-primary-foreground whitespace-pre-wrap'
                      : 'glass-lite border border-border/40'
                  }`}
                >
                  {m.role === 'assistant' ? <Markdown text={m.content} /> : m.content}
                </div>
              </motion.div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="glass-lite border border-border/40 px-3 py-2 rounded-lg text-xs">
                  <span className="inline-block animate-pulse">Thinking…</span>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="px-5 py-2 text-xs text-destructive border-t border-destructive/40">
              {error}
            </div>
          )}

          <div className="px-5 py-4 border-t border-border/40 flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              placeholder="Ask about risks, breaches, DPDPA, decisions…"
              className="flex-1 glass-lite px-3 py-2 rounded-md text-xs border border-border/40 focus:outline-none focus:border-primary"
              disabled={sending}
            />
            <Button data-action="disc.frontier_chat.a01" size="sm" onClick={() => void send()} disabled={sending || !input.trim()}>
              <Send className="h-3 w-3 mr-1" />
              Send
            </Button>
          </div>
        </GlassCard>

        {/* Side panel: sample questions + status */}
        <div className="space-y-4">
          <GlassCard className="p-5">
            <CardHeader className="p-0 mb-3">
              <CardTitle className="text-sm">Try a question</CardTitle>
              <CardDescription clamp>Tap to send. Each response is grounded on platform surfaces.</CardDescription>
            </CardHeader>
            <div className="space-y-2">
              {SAMPLE_QUESTIONS.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => void send(q)}
                  disabled={sending}
                  className="w-full text-left glass-lite px-3 py-2 rounded-md text-xs hover:border-primary border border-transparent transition-colors disabled:opacity-50"
                >
                  <Rx>{q}</Rx>
                </button>
              ))}
            </div>
          </GlassCard>

          <GlassCard className="p-5">
            <CardHeader className="p-0 mb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Info className="h-4 w-4 text-info" /> How this works
              </CardTitle>
            </CardHeader>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Responses are produced by a deterministic responder that pattern-matches your
              question against the platform&apos;s surfaces (findings, breaches, decisions,
              <Rx>DSAR</Rx>, compliance coverage). To upgrade to live AI on your tenant, register a
              BYO-LLM key on{' '}
              <a className="text-primary underline" href="/frontier/byo-llm">
                /frontier/byo-llm
              </a>{' '}
              - Anthropic / Azure OpenAI / Bedrock / Vertex / Mistral / Cohere / on-prem
              vLLM all supported. Keys live in your VPC KMS; the controller stores only
              kms:// references.
              {modelHint && (
                <span className="block mt-2 text-success">
                  ✓ Active key: <code>{modelHint}</code>
                </span>
              )}
            </p>
          </GlassCard>
        </div>
      </div>
    </PageContainer>
  );
}

/**
 * Minimal markdown renderer scoped to the patterns the chat backend
 * emits: **bold**, `code`, [text](url) links, numbered + bullet lists,
 * and paragraph breaks. We avoid a dependency because the surface is
 * small and deterministic.
 */
function Markdown({ text }: { text: string }): JSX.Element {
  // Split into blocks separated by blank lines.
  const blocks = text.split(/\n\s*\n/);
  return (
    <div className="space-y-2">
      {blocks.map((block, bi) => {
        const lines = block.split('\n').map((l) => l.trimEnd());
        const numbered = lines.every((l) => /^\d+\.\s+/.test(l));
        const bulleted = lines.every((l) => /^[-*]\s+/.test(l));
        if (numbered && lines.length > 1) {
          return (
            <ol key={bi} className="list-decimal pl-5 space-y-1">
              {lines.map((l, li) => (
                <li key={li}>{renderInline(l.replace(/^\d+\.\s+/, ''))}</li>
              ))}
            </ol>
          );
        }
        if (bulleted && lines.length > 1) {
          return (
            <ul key={bi} className="list-disc pl-5 space-y-1">
              {lines.map((l, li) => (
                <li key={li}>{renderInline(l.replace(/^[-*]\s+/, ''))}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={bi} className="leading-relaxed">
            {lines.map((l, li) => (
              <span key={li}>
                {renderInline(l)}
                {li < lines.length - 1 && <br />}
              </span>
            ))}
          </p>
        );
      })}
    </div>
  );
}

/**
 * Inline renderer: handles **bold**, `code`, and [label](url) links.
 * Tokens are matched non-greedily; anything outside a match is a plain
 * text run.
 */
function renderInline(text: string): JSX.Element[] {
  const out: JSX.Element[] = [];
  const re = /(\*\*([^*]+)\*\*)|(`([^`]+)`)|(\[([^\]]+)\]\(([^)]+)\))/g;
  let cursor = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > cursor) {
      out.push(<span key={key++}>{text.slice(cursor, match.index)}</span>);
    }
    if (match[2] !== undefined) {
      out.push(<strong key={key++}>{match[2]}</strong>);
    } else if (match[4] !== undefined) {
      out.push(
        <code key={key++} className="font-mono text-[11px] bg-muted/40 px-1 rounded">
          {match[4]}
        </code>,
      );
    } else if (match[6] !== undefined && match[7] !== undefined) {
      out.push(
        <a
          key={key++}
          href={match[7]}
          className="text-primary underline underline-offset-2"
          target={match[7].startsWith('http') ? '_blank' : undefined}
          rel={match[7].startsWith('http') ? 'noreferrer' : undefined}
        >
          {match[6]}
        </a>,
      );
    }
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) {
    out.push(<span key={key++}>{text.slice(cursor)}</span>);
  }
  return out;
}
